import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFetchForSDS } from './useFetchForSDS';

interface BindleResource {
	type: string;
	namespace: string;
	action: string;
	ids: string[];
}

interface AuthorizeAPIResponse {
	user: string;
	requestId: string;
	authorizedResource: {
		type: string;
		namespace: string;
		action: string;
		ids: string[];
	};
}

/**
 * Generic hook to check Bindle authorization for a given resource
 * @param userName - The username to check authorization for
 * @param resource - The Bindle resource object containing type, namespace, action, and ids
 * @returns Object containing isAuthorized flag, loading state, and error
 */
export function useBindleAuthorization(
	userName: string | undefined,
	resource: BindleResource
) {
	const {
		isLoading: isCheckingPermission,
		error: permissionError,
		data: authResponse,
	} = useFetchForSDS<AuthorizeAPIResponse>({
		path: '/authorize',
		method: 'POST',
		body: {
			user: userName,
			resource,
			requestId: uuidv4(),
		},
	});

	const isAuthorized = useMemo(() => {
		if (!authResponse) return false;
		const authorizedIds = authResponse.authorizedResource?.ids || [];

		return authorizedIds.length > 0;
	}, [authResponse]);

	return { isAuthorized, isCheckingPermission, permissionError };
}
